import React from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { SIDEBAR_ITEMS } from '../../../utils/Constants';
import { CheckCircle2, Settings2, Target, Share2, Rss, ArrowRight, RefreshCw, Lock } from 'lucide-react';
import { AnimatedIconButton } from '../../common/AnimatedIconButton';
import { useState } from 'react';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { AnimatePresence, motion } from 'framer-motion';

interface SetupWizardProps {
  onOpenSidebarTab: (tabId: string) => void;
  onRefresh?: () => Promise<void>;
  steps: {
    hasPositioning: boolean;
    hasWatchTopic: boolean;
    hasSource: boolean;
    hasChannel: boolean;
  };
  /** V4-5 — true si l'utilisateur n'a aucune charte graphique (palette/font/image) configurée.
   *  Quand toutes les 4 étapes sont complétées + ce flag est true, on affiche un
   *  CTA "Définir ta charte graphique" pour que les agents IA soient cohérents
   *  même sans landing page scrapée. */
  brandConfigMissing?: boolean;
  /** V4-WIZARD-02 2026-05-29 — callback optionnel appelé quand l'utilisateur clique
   *  le CTA de fin (4/4 étapes complétées). Si non fourni, le bouton n'est pas affiché. */
  onComplete?: () => void;
  /** V4-WIZARD-AGENCY-CONTEXT 2026-06-18 — type de compte ('USER' | 'AGENCY' | 'GUEST').
   *  Si 'AGENCY', on affiche un encart contextuel en tête clarifiant DE QUI on
   *  configure le positionnement (l'espace courant = un client de l'agence). */
  accountType?: string;
  /** Nom de l'espace courant — injecté dans l'encart agence. */
  spaceName?: string;
}

// Note 20-05 — Le wizard est PER-SPACE par design : il évalue les `steps` passés
// en props (qui viennent du space courant via useSetupWizard). Quand une agence
// switch vers un space client non configuré, le wizard se relance pour CE space.
// Pas de "skip global pour agency" — chaque space mérite son propre setup.

export function SetupWizard({ onOpenSidebarTab, steps, onRefresh, brandConfigMissing, onComplete, accountType, spaceName }: SetupWizardProps) {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // V4-WIZARD-AGENCY-CONTEXT 2026-06-18 — encart contextuel pour les comptes agence.
  const isAgency = accountType === 'AGENCY';
  const agencyContextDesc = (t.setupWizard?.agencyContextDesc
    ?? 'Vous configurez l\'espace « {spaceName} ». Le positionnement, les centres d\'intérêt et les sources ci-dessous s\'appliquent à ce client.')
    .replace('{spaceName}', spaceName ?? '');

  const handleRefresh = async () => {
    if (!onRefresh) return;
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  const wizardSteps = [
    {
      id: 'positioning',
      title: t.setupWizard?.stepPositioning || 'Positionnement',
      description: t.setupWizard?.stepPositioningDesc || 'Définissez votre ligne éditoriale.',
      icon: Settings2,
      tabId: SIDEBAR_ITEMS.POSITIONING,
      isComplete: steps.hasPositioning,
    },
    {
      id: 'watch',
      title: t.setupWizard?.stepWatch || 'Centres d\'intérêt',
      description: t.setupWizard?.stepWatchDesc || 'Ajoutez au moins un centre d\'intérêt.',
      icon: Target,
      tabId: SIDEBAR_ITEMS.WATCH,
      isComplete: steps.hasWatchTopic,
    },
    {
      id: 'sources',
      title: t.setupWizard?.stepSources || 'Sources',
      description: t.setupWizard?.stepSourcesDesc || 'Connectez au moins une source de contenu.',
      icon: Rss,
      tabId: SIDEBAR_ITEMS.SOURCES,
      isComplete: steps.hasSource,
    },
    {
      id: 'channels',
      title: t.setupWizard?.stepChannels || 'Canaux',
      description: t.setupWizard?.stepChannelsDesc || 'Configurez au moins un canal de communication.',
      icon: Share2,
      tabId: SIDEBAR_ITEMS.CHANNELS,
      isComplete: steps.hasChannel,
    }
  ];

  return (
    <div className="flex flex-col items-center justify-center h-full w-full overflow-x-hidden px-3 py-6 sm:px-8 sm:py-8" data-testid="setup-wizard">
      <div className="w-full max-w-2xl px-4 py-6 sm:px-8 sm:py-8" style={{ backgroundColor: 'var(--color-background)', borderRadius: '1rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
        <div className="flex justify-between items-center mb-3 gap-2">
          <h2 className="text-lg sm:text-2xl font-bold text-center flex-1" style={{ color: 'var(--color-text-primary)' }}>
            {t.setupWizard?.title || 'Bienvenue sur Raiso'}
          </h2>
          <div className="flex-shrink-0">
            <AnimatedIconButton
              icon={RefreshCw}
              label={t.common?.refresh ?? 'Actualiser'}
              onClick={handleRefresh}
              loading={isRefreshing}
              variant="neutral"
              size="default"
              title={t.common?.refresh ?? 'Actualiser'}
              testId="setup-wizard-refresh"
            />
          </div>
        </div>
        
        <p className="text-center text-sm sm:text-base mb-3" style={{ color: 'var(--color-text-secondary)' }}>
          {t.setupWizard?.subtitle || 'Complétez ces 4 étapes pour commencer à générer du contenu.'}
        </p>

        {/* V4-WIZARD-AGENCY-CONTEXT 2026-06-18 — encart contextuel agence : clarifie que
            la config s'applique à l'espace courant (= un client de l'agence). Volontairement
            un encart, PAS une 5ᵉ étape lockable, pour ne pas casser la barre de progression
            4 étapes ni la logique de lock séquentiel. */}
        {isAgency && (
          <div
            className="mb-4 p-3 rounded-lg flex items-start gap-2"
            style={{ backgroundColor: 'var(--color-primary-light)', border: '1px solid var(--color-primary)' }}
            data-testid="setup-wizard-agency-context"
          >
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
                {t.setupWizard?.agencyContextTitle ?? '👥 Espace client'}
              </h4>
              <p className="text-xs sm:text-sm mt-1" style={{ color: 'var(--color-text-primary)' }}>
                {agencyContextDesc}
              </p>
            </div>
          </div>
        )}

        {/* UX backlog 8.1 : barre de progression visuelle + compteur "X/N étapes complétées".
            La barre se remplit au fil des étapes ; les checks pulsent à la complétion. */}
        {(() => {
          const completed = wizardSteps.filter(s => s.isComplete).length;
          const total = wizardSteps.length;
          const pct = (completed / total) * 100;
          return (
            <div className="mb-6">
              <div className="flex items-center justify-between text-xs mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                <span>
                  {(t.setupWizard?.progressLabel ?? '{completed}/{total} étapes complétées')
                    .replace('{completed}', String(completed))
                    .replace('{total}', String(total))}
                </span>
                <span style={{ color: 'var(--color-primary)' }} className="font-semibold">{Math.round(pct)}%</span>
              </div>
              <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-border)' }}>
                <div
                  className="h-full transition-all duration-500"
                  style={{ width: `${pct}%`, backgroundColor: 'var(--color-primary)' }}
                />
              </div>
            </div>
          );
        })()}

        {/* Backlog UX 19-05 — lock séquentiel des steps : on ne peut pas
            sauter une étape. Un step est "locked" si la step précédente n'est
            pas complète. Le state apparaît grisé avec une icône cadenas + un
            tooltip qui dirige l'utilisateur vers la step manquante. */}
        <div className="space-y-3 sm:space-y-4">
          <AnimatePresence initial={true}>
          {wizardSteps.map((step, index) => {
            const previousComplete = index === 0 ? true : wizardSteps[index - 1].isComplete;
            const isLocked = !step.isComplete && !previousComplete;
            const lockTooltip = isLocked
              ? (t.setupWizard?.lockedStepTooltip
                  ?? 'Complétez d\'abord l\'étape "{previousTitle}" pour débloquer celle-ci.')
                  .replace('{previousTitle}', wizardSteps[index - 1]?.title ?? '')
              : undefined;
            const Icon = step.icon;
            return (
              <motion.div
                key={step.id}
                layout
                initial={{ opacity: 0, x: -16 }}
                animate={{
                  opacity: step.isComplete ? 0.7 : isLocked ? 0.55 : 1,
                  x: 0,
                }}
                transition={{
                  duration: 0.3,
                  ease: [0.22, 1, 0.36, 1], // smooth ease-out, cohérent avec l'identité Raiso
                  delay: index * 0.05,
                }}
                className="flex items-center gap-3 p-3 sm:p-4 rounded-lg transition-colors border"
                data-testid={`setup-wizard-step-${step.id}`}
                style={{
                  borderColor: 'var(--color-border)',
                  backgroundColor: step.isComplete ? 'var(--color-surface)' : 'transparent',
                }}
                title={lockTooltip}
              >
                {/* UX backlog 8.1 : check qui pulse à la complétion via motion.div.
                    Backlog 19-05 : icône cadenas sur les steps verrouillés. */}
                <motion.div
                  key={`step-icon-${step.id}-${step.isComplete ? 'done' : isLocked ? 'locked' : 'pending'}`}
                  initial={step.isComplete ? { scale: 0.85 } : false}
                  animate={step.isComplete ? { scale: [0.85, 1.25, 1] } : { scale: 1 }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                  className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full flex-shrink-0"
                  style={{
                    backgroundColor: step.isComplete
                      ? 'var(--color-success-light)'
                      : isLocked
                        ? 'var(--color-border)'
                        : 'var(--color-primary-light)',
                    color: step.isComplete
                      ? 'var(--color-success)'
                      : isLocked
                        ? 'var(--color-text-secondary)'
                        : 'var(--color-primary)',
                  }}
                >
                  {step.isComplete
                    ? <CheckCircle2 size={20} />
                    : isLocked
                      ? <Lock size={16} />
                      : <span className="font-bold text-sm">{index + 1}</span>}
                </motion.div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-sm sm:text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {step.title}
                  </h3>
                  <p className="text-xs sm:text-sm leading-snug" style={{ color: 'var(--color-text-secondary)' }}>
                    {isLocked
                      ? (t.setupWizard?.lockedStepDescription
                          ?? 'Disponible après l\'étape précédente.')
                      : step.description}
                  </p>
                </div>

                {!step.isComplete && (
                  <div className="flex-shrink-0">
                    <AnimatedIconButton
                      icon={isLocked ? Lock : ArrowRight}
                      label={isMobile ? undefined : (isLocked ? (t.setupWizard?.lockedStepBadge ?? 'Verrouillé') : (t.setupWizard?.buttonConfigure ?? 'Configurer'))}
                      title={isLocked ? lockTooltip : (t.setupWizard?.buttonConfigure ?? 'Configurer')}
                      onClick={isLocked ? undefined : () => onOpenSidebarTab(step.tabId)}
                      disabled={isLocked}
                      variant={isLocked ? 'neutral' : 'default'}
                      testId={`setup-wizard-step-${step.id}-action`}
                    />
                  </div>
                )}
              </motion.div>
            );
          })}
          </AnimatePresence>
        </div>

        {/* V4-5 — Quand les 4 étapes principales sont OK + pas de charte graphique
            renseignée, on invite explicitement l'utilisateur à la saisir manuellement.
            Sans ça, les agents IA générant des carrousels/images n'ont aucune
            cohérence visuelle avec la marque. */}
        {steps.hasPositioning && steps.hasWatchTopic && steps.hasSource && steps.hasChannel && brandConfigMissing && (
          <div
            className="mt-6 p-4 rounded-lg flex items-center gap-3"
            style={{
              backgroundColor: 'var(--color-primary-light)',
              border: '1px solid var(--color-primary)',
            }}
          >
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
                {t.setupWizard?.brandConfigCtaTitle ?? '🎨 Une dernière chose : votre charte graphique'}
              </h4>
              <p className="text-xs sm:text-sm mt-1" style={{ color: 'var(--color-text-primary)' }}>
                {t.setupWizard?.brandConfigCtaDesc
                  ?? 'Ajoutez manuellement vos couleurs, fonts et images pour que les visuels générés par l\'IA soient cohérents avec votre identité.'}
              </p>
            </div>
            <AnimatedIconButton
              icon={ArrowRight}
              label={isMobile ? undefined : (t.setupWizard?.brandConfigCtaButton ?? 'Configurer')}
              title={t.setupWizard?.brandConfigCtaButton ?? 'Configurer'}
              // V4-WIZARD-03 2026-05-29 — On signale via querystring `mode=brand`
              // que PositioningTab doit ouvrir directement le mode "Charte graphique".
              // pushState évite un reload, l'onglet Positioning lira le param au mount.
              onClick={() => {
                try {
                  const url = new URL(window.location.href);
                  url.searchParams.set('mode', 'brand');
                  window.history.pushState({}, '', url.toString());
                } catch (e) {
                  // pas de window.URL ? on continue sans crasher
                }
                onOpenSidebarTab(SIDEBAR_ITEMS.POSITIONING);
              }}
              variant="default"
              testId="setup-wizard-brand-config-action"
            />
          </div>
        )}

        {/* V4-WIZARD-02 2026-05-29 — CTA fin de wizard quand 4/4 sont OK.
            Permet à l'utilisateur de quitter explicitement le wizard pour aller
            générer des posts. Affiché uniquement si `onComplete` callback fourni. */}
        {steps.hasPositioning && steps.hasWatchTopic && steps.hasSource && steps.hasChannel && onComplete && (
          <div className="mt-6 flex justify-center">
            <AnimatedIconButton
              icon={ArrowRight}
              label={t.setupWizard?.startCTA ?? t.setupWizard?.buttonStart ?? 'Commencer à générer des posts'}
              title={t.setupWizard?.startCTA ?? t.setupWizard?.buttonStart ?? 'Commencer à générer des posts'}
              onClick={onComplete}
              variant="default"
              testId="setup-wizard-start-cta"
            />
          </div>
        )}
      </div>
    </div>
  );
}
